/**
 * Binding a live stream to a `<video>` that React may build more than once.
 *
 * The obvious way to do this — a `useRef`, and an effect keyed on the stream
 * that assigns `srcObject` — is wrong in exactly one case, and it is the case a
 * call spends its whole life in. A minimised call unmounts the overlay's
 * elements while the component that owns their refs stays mounted: the stream
 * is the same object it always was, so the effect does not run again, and the
 * ref now points at a brand-new `<video>` nobody ever handed a source to. The
 * call is fine, the bytes keep arriving, and the screen is black until it ends.
 * The same shape appears wherever an element comes and goes under an unchanged
 * stream — the outgoing screen becoming the active one, a camera switched off
 * and on again.
 *
 * So the element is the dependency, not just the stream. A callback ref that
 * puts the node into state is what makes it one: React calls it with the node
 * on mount and with null on unmount, which is the event a `useRef` swallows.
 * Everything else follows from that — the source is re-bound and the paint
 * watchdog is re-armed on the element that is actually on screen.
 *
 * `keepPaintingWhileVisible` is the second half and is documented in
 * livePreview.ts: a WebView that was put away comes back with elements holding
 * live streams and painting nothing, and re-attaching the stream is the repair.
 * That watchdog can only repair the element it was given, which is the other
 * reason it has to be started afresh for each one.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { keepPaintingWhileVisible, type PreviewTrouble } from './livePreview.ts';

export function useLivePicture(
  stream: MediaStream | null,
  onTrouble: (trouble: PreviewTrouble) => void,
): (node: HTMLVideoElement | null) => void {
  const [element, setElement] = useState<HTMLVideoElement | null>(null);

  // Through a ref rather than as a dependency: a caller that rebuilds this
  // callback every render would otherwise re-attach the source every render,
  // which is a flicker at best and a fresh watchdog budget every frame at worst.
  const report = useRef(onTrouble);
  report.current = onTrouble;

  const attach = useCallback((node: HTMLVideoElement | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element) return;
    // A property, not an attribute — the reason every binding here is a ref.
    element.srcObject = stream;
    // Nothing to watch, and nothing to keep painting. The assignment above
    // still happened, so an element whose stream went away is cleared rather
    // than left holding the last one.
    if (!stream) return;
    return keepPaintingWhileVisible(element, (trouble) => report.current(trouble));
  }, [element, stream]);

  return attach;
}
